import { Component, Input } from '@angular/core';
import { Role, User } from '../Models/User';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { compareSync, genSaltSync, hashSync, genSalt, hash, compare } from "bcrypt-ts";
import { Observable, lastValueFrom } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
    selector: 'app-admin-users',
    templateUrl: './admin-users.component.html',
    styleUrl: './admin-users.component.css',
    standalone: false
})
export class AdminUsersComponent {
  @Input({ required: true }) loggedInUser?: User = undefined;
  private loggedInUserTag = "";

  // transient plain-text password store for generated passwords (not displayed)
  private generatedPasswords = new Map<number, string>();

  constructor(private http: HttpClient) { }

  users: User[] = [];

  roles: Role[] = [];

  tableUsers = new MatTableDataSource<User>([]);

  displayedColumns: string[] = ["UserName", "FirstName", "LastName", "Email", "Roles", "Password", "Action"];

  ngOnInit() {

    this.loggedInUserTag = `${this.loggedInUser?.lastName}, ${this.loggedInUser?.firstName}`;

    this.getUsers();

    this.getRoles();

  }

  getUsers() {


    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag);

    this.http.get<User[]>('/charitycentral/getallusers', { params: parameters }).subscribe(
      (result) => {

        const sorted = (result || []).map(u => ({
          ...u,
          roles: (u.roles || []).filter((r, idx, arr) => arr.findIndex(x => x.id === r.id) === idx)
        })).slice().sort((a, b) =>
          (a.firstName || '').localeCompare(b.firstName || '', undefined, { sensitivity: 'base' })
        );
        this.users = sorted;
        this.tableUsers.data = sorted;

      },
      (error) => {
        // Error handled silently
      }
    );
  }

  userHasRole(user: User, role: Role) {
    // Check if the user has this role in their roles array
    if (user.roles && user.roles.some(r => r.id === role.id)) {
      return true;
    }
    else {
      return false;
    }
  }

  toggleUserRole(event: MatCheckboxChange, user: User, role: Role) {

    var userToChangeRole = this.users.filter(x => x.id == user.id)[0];

    if (!userToChangeRole.roles) {
      userToChangeRole.roles = [];
    }

    if (event.checked) {
      // Add role to the array if not already present
      if (!userToChangeRole.roles.some(r => r.id === role.id)) {
        userToChangeRole.roles.push(role);
      }
    }
    else {
      // Remove role from the array
      userToChangeRole.roles = userToChangeRole.roles.filter(r => r.id !== role.id);
    }
  }


  getRoles() {


    let parameters = new HttpParams()
      .set('user', this.loggedInUserTag);

    this.http.get<Role[]>('/charitycentral/getallroles', { params: parameters }).subscribe(
      (result) => {

        this.roles = result;

      },
      (error) => {
        // Error handled silently
      }
    );
  }

  editUser(user: User) {

    //JP set all the other reports to not editable first
    this.tableUsers.data.forEach(report => {

      report.isEdit = false;

    })

    user.isEdit = true;

  }

  deleteUser(user: User) {

    let parameters = new HttpParams()
      .set('userId', user.id)
      .set('user', this.loggedInUserTag);

    this.http.post<boolean>("/charitycentral/deleteuser", null, { params: parameters }).subscribe(
      (result) => {

        if (result == true) {

          this.tableUsers.data = this.tableUsers.data.filter(x => x.id != user.id);

        }

      });

  }

  saveUser(user: User) {

    var updatePassword = false;

    // If we have a generated password stored for this user, treat it as the password to update
    let plainPassword: string | undefined = undefined;
    if (this.generatedPasswords.has(user.id)) {
      plainPassword = this.generatedPasswords.get(user.id)!;
    }

    if ((user.password != undefined && user.password != "") || plainPassword) {

      updatePassword = true;

      //JP get the hashed version of the password if it is set
      var hashedPassword = hashSync(plainPassword ?? user.password ?? '');

      user.password = "";

    }

    let userParameters = new HttpParams()
      .set('userbeingprocessed', JSON.stringify(user))
      .set('user', this.loggedInUserTag);

    //JP this is an "add"
    if (user.id === 0) {

      this.http.post<number>("/charitycentral/createuser", null, { params: userParameters }).subscribe(
        (newUserId) => {

          if (newUserId > 0) {

            //JP user was created, now set the password. there must be a password for a create
            this.updatePassword(newUserId, hashedPassword).then((result) => {

              if (result == true) {

                //JP reset the user list
                this.getUsers();

              }

            });
           
          }

        });

    }
    else {

      //JP this is an "update"
      this.http.post<boolean>("/charitycentral/updateuser", null, { params: userParameters }).subscribe(
        (saveResult) => {

          //JP has the user update saved? Do we need to update the password as well?
          if (saveResult == true && updatePassword == true) {

            this.updatePassword(user.id, hashedPassword).then((result) => {

            });

          }

          user.isEdit = false;

          // clear generated password after save
          if (this.generatedPasswords.has(user.id)) this.generatedPasswords.delete(user.id);

        });
    }
  }

  async updatePassword(userId: number, hashedPassword: string) {

    let passwordParameters = new HttpParams()
      .set('userid', userId)
      .set('password', hashedPassword)
      .set('user', this.loggedInUserTag);

    var response = this.http.post<boolean>("/charitycentral/updateuserpassword", null, { params: passwordParameters });

    return await lastValueFrom(response);


  }

  cancelUserEdit(user: User) {

    // If this is a newly added user (id ===0) remove it from the lists, otherwise just exit edit mode
    if (user.id ===0) {
      this.users = this.users.filter(u => u !== user);
      this.tableUsers.data = this.users;
    } else {
      user.isEdit = false;
    }

  }

  addUser() {

    //JP only allow one new item in the list at once
    if (this.users.findIndex(obj => obj.id === 0) === -1) {

      this.users.unshift({ id: 0, username: '', firstName: '', lastName: '', email: '', password: '', isEdit: true, roles: [] });
      this.tableUsers.data = this.users;

    }

  }

  // Helper used by mat-select to compare roles by id instead of object reference
  compareRoles(a: Role | null, b: Role | null): boolean {
    if (!a || !b) return false;
    return a.id === b.id;
  }

  // Generate a random strong password, set to user.password (masked in UI) and copy to clipboard
  generatePassword(user: User) {
    const pwd = this.randomPassword(10);
    this.generatedPasswords.set(user.id, pwd);
    user.password = pwd;

    // copy to clipboard if supported
    if (navigator && (navigator as any).clipboard && (navigator as any).clipboard.writeText) {
      (navigator as any).clipboard.writeText(pwd).catch(() => { /* ignore clipboard failures */ });
    }
  }

  private randomPassword(length: number) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+';
    let pass = '';
    const array = new Uint32Array(length);
    if (window && (window.crypto || (window as any).msCrypto)) {
      const cryptoObj = window.crypto || (window as any).msCrypto;
      cryptoObj.getRandomValues(array);
      for (let i =0; i < length; i++) {
        pass += chars[array[i] % chars.length];
      }
    } else {
      for (let i =0; i < length; i++) pass += chars[Math.floor(Math.random() * chars.length)];
    }
    return pass;
  }

}

